import { useState } from "react";
import { ScrollText, Sparkles, ChevronDown, ArrowDownToLine } from "lucide-react";
import { jumpToTimeline } from "@/lib/phaseNav";

/**
 * ExecutiveSummary — renders a prominent card containing the AI-generated
 * 4-6 sentence summary of the project: goals, required work, technical focus,
 * timeline & commercial frame. Falls back gracefully when the field is missing.
 */
export default function ExecutiveSummary({ summary, complexity, hoursMin, hoursMax, totalWeeks }) {
  const [expanded, setExpanded] = useState(true);
  if (!summary || !summary.trim()) return null;

  // Parse structured executive summary into phases.
  // Expected format:
  //   1. Phasen-Titel
  //      - Aufgabe 1
  //      - Aufgabe 2
  //   2. Naechste Phase
  //      - ...
  // Lines that don't match either pattern are appended to the previous phase
  // as a free-text intro line, or — if no phase exists yet — collected as
  // pre-amble paragraphs.
  const parseSteps = (txt) => {
    const lines = txt.replace(/\r\n/g, "\n").split("\n");
    const phases = [];
    const preamble = [];
    let current = null;
    const phaseRe = /^\s*(\d+)[\.\)]\s+(.+?)\s*$/;
    const bulletRe = /^\s*[-•*]\s+(.+?)\s*$/;
    for (const raw of lines) {
      const line = raw.trimEnd();
      if (!line.trim()) {
        continue; // blank lines skipped
      }
      const phaseMatch = line.match(phaseRe);
      const bulletMatch = line.match(bulletRe);
      if (phaseMatch && !bulletMatch) {
        if (current) phases.push(current);
        current = { num: phaseMatch[1], title: phaseMatch[2].replace(/^\*\*|\*\*$/g, ""), bullets: [] };
      } else if (bulletMatch) {
        if (current) {
          current.bullets.push(bulletMatch[1].replace(/^\*\*|\*\*$/g, ""));
        } else {
          preamble.push(bulletMatch[1]);
        }
      } else {
        // free text — append to current phase as note, otherwise to preamble
        if (current) current.bullets.push(line.trim());
        else preamble.push(line.trim());
      }
    }
    if (current) phases.push(current);
    return { phases, preamble };
  };

  // Strip residual <b>...</b> from older entries
  const cleanText = (s) => s.replace(/<\/?b>/g, "");
  const { phases, preamble } = parseSteps(cleanText(summary));
  const isStructured = phases.length > 0;
  const goToPhase = jumpToTimeline;

  return (
    <div data-testid="executive-summary" className="relative rounded-xl overflow-hidden border border-[#00F0FF]/20">
      {/* gradient backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/[0.07] via-[#0A0A14] to-[#0A0A14] pointer-events-none" />
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#00F0FF]/5 blur-3xl pointer-events-none" />

      <div className="relative p-5 sm:p-6">
        {/* Header */}
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          data-testid="exec-summary-toggle"
          className="w-full flex items-center justify-between gap-3 text-left group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/20 flex-shrink-0">
              <ScrollText className="w-4 h-4 text-[#00F0FF]" />
            </div>
            <div>
              <span className="text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.25em] uppercase flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Executive Summary
              </span>
              <h2 className="font-['Outfit'] text-base font-semibold text-white mt-0.5">
                Notwendige Arbeiten zur Zielerreichung
              </h2>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-[#A0A0B0] transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>

        {/* Body */}
        {expanded && (
          <div className="mt-4 pl-13 sm:pl-[52px]">
            {/* Optional preamble (free text before the first phase) */}
            {preamble.length > 0 && (
              <p className="text-sm sm:text-[15px] text-[#D0D0DA] leading-relaxed mb-5">
                {preamble.join(" ")}
              </p>
            )}

            {isStructured ? (
              <ol data-testid="exec-summary-steps" className="space-y-5">
                {phases.map((p, i) => (
                  <li
                    key={i}
                    id={`exec-phase-num-${p.num}`}
                    data-testid={`exec-phase-${i}`}
                    className="relative cursor-pointer group rounded-lg -ml-2 pl-14 py-2 transition-colors hover:bg-[#00F0FF]/[0.05]"
                    onClick={() => goToPhase(p.num)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goToPhase(p.num);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Springe zu Meilenstein-Phase ${p.num}`}
                    title={`Springe zu Phase ${p.num} im Gantt & in der Meilenstein-Liste`}
                  >
                    {/* Numbered chip */}
                    <span
                      className="absolute left-2 top-2 w-9 h-9 rounded-lg flex items-center justify-center font-['JetBrains_Mono'] text-[13px] font-bold border transition-all group-hover:scale-105 group-hover:shadow-[0_0_12px_rgba(0,240,255,0.4)]"
                      style={{
                        background: "rgba(0,240,255,0.10)",
                        borderColor: "rgba(0,240,255,0.35)",
                        color: "#00F0FF",
                      }}
                    >
                      {p.num}
                    </span>
                    {/* Title row with link indicator */}
                    <div className="flex items-center gap-2">
                      <h3 className="font-['Outfit'] text-[15px] sm:text-base font-semibold text-white leading-snug">
                        {p.title}
                      </h3>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider"
                        aria-hidden="true"
                      >
                        <ArrowDownToLine className="w-3 h-3" />
                        Zu Meilenstein
                      </span>
                    </div>
                    {/* Bullets */}
                    {p.bullets.length > 0 && (
                      <ul className="mt-2 space-y-1.5">
                        {p.bullets.map((b, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-[13.5px] text-[#C0C0CA] leading-relaxed">
                            <span
                              className="mt-[7px] w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ background: "#00F0FF" }}
                            />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p data-testid="exec-summary-text" className="text-sm sm:text-[15px] text-[#D0D0DA] leading-relaxed whitespace-pre-line">
                {cleanText(summary)}
              </p>
            )}

            {/* KPI ribbon */}
            {(complexity || hoursMin != null || totalWeeks) && (
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-['JetBrains_Mono']">
                {complexity && (
                  <span className="text-[#A0A0B0]">
                    <span className="text-[#606070]">Komplexität:</span>{" "}
                    <span className="text-white font-semibold">{complexity}</span>
                  </span>
                )}
                {hoursMin != null && hoursMax != null && (
                  <span className="text-[#A0A0B0]">
                    <span className="text-[#606070]">Aufwand:</span>{" "}
                    <span className="text-white font-semibold">
                      {hoursMin}-{hoursMax} h
                    </span>
                    <span className="text-[#606070] ml-1">
                      (≈ {Math.round(hoursMin / 8)}-{Math.round(hoursMax / 8)} T.)
                    </span>
                  </span>
                )}
                {totalWeeks > 0 && (
                  <span className="text-[#A0A0B0]">
                    <span className="text-[#606070]">Laufzeit:</span>{" "}
                    <span className="text-white font-semibold">{totalWeeks} Wochen</span>
                  </span>
                )}
                <span className="ml-auto text-[#606070]">KI-generiert · jederzeit aktualisierbar</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
